// 引入 React 与常用 Hook。
import React, { useRef, useEffect, useState } from 'react';
// 引入国际化 Hook。
import { useTranslations } from 'next-intl';
// 引入裁剪库。
import Cropper from 'cropperjs';
// 引入裁剪库样式。
import 'cropperjs/dist/cropper.css';
// 引入 className 合并工具。
import cx from 'classnames';
// 引入 Toast 组件。
import Toast from '@/components/Toast';
// 引入样式。
import Styles from './index.module.scss';
// 引入通用图标组件。
import CommonIcon from '@/components/CommonIcon';
// 引入放大图标。
import CropBigIcon from '../../../../assets/icon/account/crop-big.svg';
// 引入缩小图标。
import CropSmallIcon from '../../../../assets/icon/account/crop-small.svg';

// 头像裁剪组件入参类型。
interface AvatarCropperProps {
  imageFile: File | null;
  visible: boolean;
  onClose: () => void;
  onSave: (file: File) => Promise<void>;
}

// 头像裁剪组件。
const AvatarCropper: React.FC<AvatarCropperProps> = ({
  imageFile,
  visible,
  onClose,
  onSave,
}) => {
  // 国际化函数。
  const t = useTranslations();
  // 图片节点引用。
  const imageRef = useRef<HTMLImageElement>(null);
  // cropper 实例引用。
  const cropperRef = useRef<Cropper | null>(null);
  // 最小缩放比例。
  const minZoomRef = useRef<number>(0.1);
  // 最大缩放比例。
  const maxZoomRef = useRef<number>(5.0);
  // 滑块缩放值。
  const [zoomLevel, setZoomLevel] = useState(0); // 0 to 100
  // 保存中状态。
  const [saving, setSaving] = useState(false);
  // 预览图 URL。
  const [imageSrc, setImageSrc] = useState<string>('');

  // Generate URL for the file
  useEffect(() => {
    if (imageFile) {
      const url = URL.createObjectURL(imageFile);
      setImageSrc(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setImageSrc('');
    }
  }, [imageFile]);

  // Re-init cropper with improved logic
  useEffect(() => {
    // 不可见或资源未就绪时不初始化。
    if (!visible || !imageSrc || !imageRef.current) return;

    // 已有实例先销毁，避免重复实例。
    if (cropperRef.current) {
      cropperRef.current.destroy();
    }

    const cropper = new Cropper(imageRef.current, {
      aspectRatio: 1,
      background: false,
      cropBoxMovable: false,
      toggleDragModeOnDblclick: false,


      viewMode: 3,         // 限制裁剪框不超过画布的大小
      dragMode: 'move',    // 允许拖动图片
      center: false,       // 隐藏裁剪框中心的指示器
      autoCropArea: 1,     // 自动裁剪区域为整个图像
      guides: false,       // 隐藏网格线
      cropBoxResizable: false, // 禁止调整裁剪框大小
      zoomable: true,      // 允许缩放图像
      movable: true,       // 允许移动图像

      ready() {
        // 读取图片初始缩放信息。
        const imageData = cropper.getImageData();
        const initialRatio = imageData.width / imageData.naturalWidth;
        
        // Update min/max zoom based on initial fit
        minZoomRef.current = initialRatio;
        maxZoomRef.current = Math.max(initialRatio * 5, 5.0); // Ensure max is reasonably large
        
        setZoomLevel(0);
      },
      zoom(event) {
        // 读取目标缩放比例。
        const ratio = event.detail.ratio;
        const min = minZoomRef.current;
        const max = maxZoomRef.current;
        
        if (ratio < min) {
          event.preventDefault();
          // Check if we are already at min to avoid loop/redundant calls
          const currentData = cropper.getImageData();
          const currentRatio = currentData.width / currentData.naturalWidth;
          if (Math.abs(currentRatio - min) > 0.001) {
             cropper.zoomTo(min);
          }
          return;
        }
        if (ratio > max) {
          event.preventDefault();
           // Check if we are already at max
          const currentData = cropper.getImageData();
          const currentRatio = currentData.width / currentData.naturalWidth;
          if (Math.abs(currentRatio - max) > 0.001) {
             cropper.zoomTo(max);
          }
          return;
        }

        let val = ((ratio - min) / (max - min)) * 100;
        val = Math.max(0, Math.min(100, val));
        // 回写滑块值。
        setZoomLevel(val);
      }
    });

    // 保存实例引用。
    cropperRef.current = cropper;

    return () => {
      // 卸载时销毁实例并清引用。
      cropper.destroy();
      cropperRef.current = null;
    };
  }, [visible, imageSrc]);

  // 处理滑块缩放。
  const onSliderInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setZoomLevel(val);
    if (cropperRef.current) {
      const min = minZoomRef.current;
      const max = maxZoomRef.current;
      const ratio = min + (val / 100) * (max - min);
      cropperRef.current.zoomTo(ratio);
    }
  }

  // 处理缩放按钮点击。
  const handleZoomBtn = (direction: 'in' | 'out') => {
    if (cropperRef.current) {
      cropperRef.current.zoom(direction === 'in' ? 0.1 : -0.1);
    }
  };

  // 处理保存。
  const handleSave = async () => {
    if (!cropperRef.current) return;
    setSaving(true);

    try {
      // 导出裁剪后的 canvas 并转 blob。
      cropperRef.current.getCroppedCanvas({
        width: 400, // Reasonable size for avatar
        height: 400,
        imageSmoothingQuality: 'high',
      }).toBlob(async (blob) => {
        if (!blob) {
          Toast.show({ content: t('user_center_avatar_cropper_crop_failed'), isPc: true });
          setSaving(false);
          return;
        }
        const file = new File([blob], "avatar.png", { type: "image/png" });
        try {
          // 上传/保存头像文件。
          await onSave(file);
        } catch (e) {
          // Error handled in onSave, but we need to catch it to stop loading state
          console.error(e);
        } finally {
          setSaving(false);
        }
      }, 'image/png');
    } catch (error) {
      console.error(error);
      Toast.show({ content: t('user_center_avatar_cropper_save_failed'), isPc: true });
      setSaving(false);
    }
  };

  // 不可见时不渲染。
  if (!visible) return null;

  return (
    <div className={Styles.cropperWrapper}>
      <div className={Styles.modal}>
        <div className={Styles.header}>
          {t('user_center_avatar_cropper_title')}
        </div>
        <div className={Styles.cropperContainer}>
          <img ref={imageRef} src={imageSrc} alt="Crop Target" style={{ opacity: 0, maxWidth: '100%' }} />
        </div>
        <div className={Styles.controls}>
          <div className={Styles.zoomBtn} onClick={() => handleZoomBtn('out')}>
            <CropSmallIcon width={24} height={24} />
          </div>
          <div className={Styles.sliderContainer}>
            <input
              type="range"
              min="0"
              max="100"
              value={zoomLevel}
              onChange={onSliderInput}
              style={{
                backgroundSize: `${zoomLevel}% 100%`
              }}
            />
          </div>
          <div className={Styles.zoomBtn} onClick={() => handleZoomBtn('in')}>
            <CropBigIcon width={24} height={24} />
          </div>
        </div>
        <div className={Styles.footer}>
          <div className={Styles.btn + ' ' + Styles.cancelBtn} onClick={onClose}>
            {t('user_center_avatar_cropper_cancel')}
          </div>
          <div className={cx(Styles.btn, Styles.saveBtn, { [Styles.loading]: saving })} onClick={!saving ? handleSave : undefined}>
            {saving ? t('user_center_avatar_cropper_saving') : t('user_center_avatar_cropper_save')}
          </div>
        </div>
      </div>
    </div>
  );
};

// 导出组件。
export default AvatarCropper;
